import { useEffect, useState } from "react";
import { CreditCard, Smartphone, QrCode, Copy, Check, Loader2 } from "lucide-react";

import { api } from "../lib/api";
import { money } from "../lib/fmt";
import { Glass, Err } from "../ui";

/**
 * PayBox — how a client actually pays, inside the portal.
 *
 * Whether anything shows here at all is the server's decision, not this
 * component's: /portal/pay/options answers with only the methods the studio has
 * switched on, and the balance re-derived from the database. A client cannot
 * make an option appear by editing anything in the browser, and cannot make
 * the amount smaller either — POST /portal/pay/order recomputes it again.
 *
 * NOTHING HERE MARKS AN INVOICE PAID
 * ----------------------------------
 * Razorpay's checkout hands this browser a payment id and a signature. Both go
 * straight to /portal/pay/verify, which checks the HMAC against the key secret
 * before a rupee is written. If this component were trusted, "I paid" would be
 * a thing anyone could type.
 *
 * UPI is deliberately two things. The `upi://` intent is what a phone should
 * use — the amount, the payee and the invoice reference are pre-filled, so
 * nobody can mistype ₹2,50,642 as ₹25,0642. The uploaded QR is for the laptop
 * case, where there is nothing to tap.
 */

const RZP_SRC = "https://checkout.razorpay.com/v1/checkout.js";

let rzpLoading = null;
function loadRazorpay() {
  if (window.Razorpay) return Promise.resolve(true);
  if (rzpLoading) return rzpLoading;
  rzpLoading = new Promise((resolve) => {
    const s = document.createElement("script");
    s.src = RZP_SRC;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.head.appendChild(s);
  });
  return rzpLoading;
}

export function PayBox({ invoice, onPaid }) {
  const [opts, setOpts] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");
  const [copied, setCopied] = useState("");
  const [showQr, setShowQr] = useState(false);

  useEffect(() => {
    let dead = false;
    api.get(`/portal/pay/options?invoice_id=${invoice.id}`)
      .then((o) => { if (!dead) setOpts(o); })
      .catch(() => { if (!dead) setOpts({ razorpay: {}, upi: {} }); });
    return () => { dead = true; };
  }, [invoice.id]);

  if (!opts) return null;

  const canCard = !!opts.razorpay?.enabled;
  const canUpi = !!opts.upi?.enabled;
  if (!canCard && !canUpi) return null;   // nothing switched on: say nothing

  const amount = Number(opts.balance || 0);

  async function payByCard() {
    setErr("");
    setBusy(true);
    try {
      const ready = await loadRazorpay();
      if (!ready) throw new Error("The payment window could not load. Check your connection, or pay by UPI.");

      const order = await api.post("/portal/pay/order", { invoice_id: invoice.id });

      await new Promise((resolve) => {
        const rzp = new window.Razorpay({
          key: order.key_id,
          order_id: order.order_id,
          amount: Math.round(order.amount * 100),
          currency: order.currency,
          name: order.name,
          description: order.description,
          prefill: order.prefill || {},
          theme: { color: "#CE3F70" },
          // The handler runs in the browser, so nothing it says is trusted.
          // Everything it hands over goes to the server to be verified.
          handler: async (r) => {
            try {
              const v = await api.post("/portal/pay/verify", {
                razorpay_order_id: r.razorpay_order_id,
                razorpay_payment_id: r.razorpay_payment_id,
                razorpay_signature: r.razorpay_signature,
              });
              setDone(v.message || "Payment received.");
              onPaid?.();
            } catch (e) {
              setErr(e.message);
            }
            resolve();
          },
          modal: {
            // Closing the window is not a failure and must not read as one.
            // The order stays 'created' and the webhook settles it if the
            // payment actually went through.
            ondismiss: () => resolve(),
          },
        });
        rzp.on("payment.failed", (e) => {
          setErr(e?.error?.description || "That payment did not go through. Nothing has been charged.");
          resolve();
        });
        rzp.open();
      });
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  const copy = (label, value) => {
    navigator.clipboard?.writeText(value).then(() => {
      setCopied(label);
      setTimeout(() => setCopied(""), 1800);
    }).catch(() => {});
  };

  if (done) {
    return (
      <Glass tint style={{ padding: 16, marginBottom: 14, display: "flex", gap: 10, alignItems: "center" }}>
        <Check size={18} color="var(--ok)" />
        <span style={{ fontSize: ".9rem" }}>{done}</span>
      </Glass>
    );
  }

  return (
    <Glass strong style={{ padding: 18, marginBottom: 16 }}>
      <p className="sec-title" style={{ padding: 0, marginTop: 0 }}>Pay invoice #{invoice.id}</p>
      <p className="hero-amt" style={{ textAlign: "left", margin: "6px 0 4px", fontSize: "1.7rem" }}>
        {money(amount)}
      </p>
      {opts.note && <p className="muted" style={{ margin: "0 0 14px", fontSize: ".82rem" }}>{opts.note}</p>}

      <Err onDismiss={() => setErr("")}>{err}</Err>

      {canCard && (
        <button className="btn primary block" style={{ marginBottom: 10 }} onClick={payByCard} disabled={busy}>
          {busy ? <Loader2 size={16} /> : <CreditCard size={16} />}
          {busy ? "Opening…" : "Pay by card or netbanking"}
        </button>
      )}

      {canUpi && (
        <>
          {opts.upi.intent && (
            <a className="btn block" style={{ marginBottom: 10 }} href={opts.upi.intent}>
              <Smartphone size={16} /> Pay {money(amount)} by UPI
            </a>
          )}

          {opts.upi.vpa && (
            <button
              className="btn block"
              style={{ marginBottom: 10 }}
              onClick={() => copy("vpa", opts.upi.vpa)}
            >
              {copied === "vpa" ? <Check size={15} color="var(--ok)" /> : <Copy size={15} />}
              <span className="mono" style={{ fontSize: ".84rem" }}>{opts.upi.vpa}</span>
            </button>
          )}

          {opts.upi.qr_url && (
            <>
              <button className="btn ghost block" onClick={() => setShowQr((v) => !v)}>
                <QrCode size={15} /> {showQr ? "Hide the QR" : "Show the QR to scan"}
              </button>
              {showQr && (
                <div style={{ textAlign: "center", marginTop: 12 }}>
                  <img
                    src={opts.upi.qr_url}
                    alt="Scan to pay the studio by UPI"
                    style={{
                      width: 200, height: 200, objectFit: "contain",
                      background: "#fff", padding: 10, borderRadius: 16,
                      boxShadow: "var(--neu-out-sm)",
                    }}
                  />
                  <p className="muted" style={{ fontSize: ".76rem", margin: "8px auto 0", maxWidth: "26ch" }}>
                    A scanned QR does not carry the amount — enter {money(amount)} yourself, and put
                    <b> DLINV{invoice.id}</b> in the note.
                  </p>
                </div>
              )}
            </>
          )}

          <p className="muted" style={{ fontSize: ".74rem", margin: "10px 0 0" }}>
            A UPI payment reaches the studio directly and is matched by hand, so the receipt here can take a
            working day to appear.
          </p>
        </>
      )}
    </Glass>
  );
}
